import './LiveServiceItem.scss';

import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useCallback, useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Flex, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { BdaService } from '../../models/BdaService';
import { ExecutionStatus } from '../../models/generics';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import UserItem from '../common/UserItem';
import { useToasts } from '../../contexts/toasts';

type Props = {
  service: BdaService;
}

function getStatusIcon(status?: ExecutionStatus) {
  switch (status) {
    case ExecutionStatus.RUNNING:
      return <FontAwesomeIcon icon={solid('cog')} spin />;
    case ExecutionStatus.STARTING:
      return <FontAwesomeIcon icon={solid('spinner')} spin />;
    case ExecutionStatus.STOPPING:
      return <FontAwesomeIcon icon={solid('ban')} spin />;
    case ExecutionStatus.STOPPED:
      return <FontAwesomeIcon icon={solid('stop')} />;
    case ExecutionStatus.COMPLETED:
      return <FontAwesomeIcon icon={regular('check-circle')} />;
    case ExecutionStatus.ERROR:
      return <FontAwesomeIcon icon={solid('exclamation-circle')} />;
    case ExecutionStatus.PENDING:
      return <FontAwesomeIcon icon={solid('hourglass-half')} />
    default:
      return <FontAwesomeIcon icon={regular('clock')} />;
  }
}

function renderStatus(status?: ExecutionStatus) {
  const statusClass = status ? `status-${status.toLowerCase()}` : 'status-unknown';
  return (
    <Flex align="center" className={`service-status ${statusClass}`}>
      {status ? getStatusIcon(status) : <FontAwesomeIcon icon={solid('circle-question')} />}
      <span className="ms-1">{status ?? 'N/A'}</span>
    </Flex>
  );
}

function LiveServiceItem({ service }: Props) {
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, 'applications'));
  const { showToast } = useToasts();

  const isExposable = useMemo(() => service.bdaApplicationId && service.exposable && service.status === ExecutionStatus.RUNNING, [service]);

  const onCopyServiceUrl = useCallback((internal: boolean, url: string) => () => {
    navigator.clipboard.writeText(url);
    showToast('text-copied', `${internal ? "Internal" : "External"} URL copied to clipboard!`);
  }, [showToast]);

  const renderUrls = useCallback(() => {
    const serviceInternalUrl = `${window.__env.EXPOSED_SERVICES_URL}/${service.bdaApplicationId}/${service.id}/`;
    const serviceExternalUrl = `bda-${service.bdaApplicationId}-${service.id}:80`;

    return (
      <Flex gap={4} align="center" justify="center" className="mt-2">
        <Tooltip title={`Internal URL: ${serviceInternalUrl}`}>
          <button className="service-workflow text-truncate w-50" onClick={onCopyServiceUrl(true, serviceInternalUrl)}>
            <FontAwesomeIcon icon={solid("copy")} className="me-2" />
            <span>{serviceInternalUrl}</span>
          </button>
        </Tooltip>
        <Tooltip title={`External URL: ${serviceExternalUrl}`}>
          <button className="service-workflow text-truncate w-50" onClick={onCopyServiceUrl(false, serviceExternalUrl)}>
            <FontAwesomeIcon icon={solid("copy")} className="me-2" />
            <span>{serviceExternalUrl}</span>
          </button>
        </Tooltip>
      </Flex>
    )
  }, [onCopyServiceUrl, service]);

  return (
    <Card className="live-service-item">
      <Flex justify="space-between" align="center" className="p-1">
        {renderStatus(service.status)}
        {service.bdaApplicationId && (
          <Tooltip title={`${applicationLabels.singular} ${service.bdaApplicationId}`} mouseEnterDelay={0.5}>
            <Link to={`/bda-applications/${service.bdaApplicationId}`} className="service-workflow">
              <FontAwesomeIcon icon={solid('diagram-project')} />
              <span className="ms-2">{service.bdaApplicationId}</span>
            </Link>
          </Tooltip>
        )}
      </Flex>

      <Card.Body className="p-2">
        <Link to={`/services/${service.id}`} className="service-link">
          <h5>{service.name}</h5>
          {service.description && (
            <Tooltip title={service.description} mouseEnterDelay={1}>
              <p className="line-clamp-2 overflow-hidden mb-0">{service.description}</p>
            </Tooltip>
          )}
        </Link>
        {isExposable && renderUrls()}
        {service.owner && <UserItem user={service.owner} organization={service.organization} className="mt-3 mb-2 sm" />}
      </Card.Body>
    </Card>
  );
}

export default memo(LiveServiceItem);
